/** Renderizador del Markdown que produce el agente.
 *
 * Propio y no una librería: el Markdown lo genera este producto, tiene una forma conocida (cuatro
 * secciones, viñetas, negritas, tablas simples) y así las citas `[ev:N]` se convierten en un chip
 * que enfoca la tarjeta. Una librería general traería su propio riesgo por una superficie que
 * aquí no existe.
 */
import type { Idioma, Tarjeta } from "../lib/tipos";
import { ChipCita } from "./ChipCita";

// Acepta `[ev:1]` (lo que escribe el motor) y `[ev_1]` (lo que un modelo copia del id de la
// tarjeta). Sin la segunda forma, las citas del modo determinista quedaban en texto plano —
// medido: 0 chips renderizados en una respuesta con 20 citas.
const CITA = /\[ev[:_]?\s*(\d+)\]/gi;

const SECCION_EVIDENCIA = /^(evidence|evidencia)$/i;

export function RespuestaMarkdown({ md, idioma, tarjetas = [], activa = null, alCitar }: {
  md: string; idioma: Idioma; tarjetas?: Tarjeta[]; activa?: string | null; alCitar?: (id: string) => void;
}) {
  const bloques = tarjetas.length ? sinSeccion(partir(md), SECCION_EVIDENCIA) : partir(md);
  const ctx: Contexto = { idioma, tarjetas, activa, alCitar };
  return (
    <div className="respuesta" lang={idioma}>
      {bloques.map((b, i) => {
        if (b.tipo === "h") return <h3 key={i}>{b.texto}</h3>;
        if (b.tipo === "lista")
          return (
            <ul key={i}>
              {b.items.map((it, j) => <li key={j}>{enLinea(it, ctx)}</li>)}
            </ul>
          );
        if (b.tipo === "numerada")
          return (
            <ol key={i}>
              {b.items.map((it, j) => <li key={j}>{enLinea(it, ctx)}</li>)}
            </ol>
          );
        if (b.tipo === "tabla")
          return (
            <div className="tabla-prosa-marco" key={i}>
              <table className="tabla-prosa">
                <thead>
                  <tr>{b.encabezados.map((h, j) => <th key={j} scope="col">{enLinea(h, ctx)}</th>)}</tr>
                </thead>
                <tbody>
                  {b.filas.map((f, j) => (
                    <tr key={j}>{f.map((c, k) => (k === 0
                      ? <th key={k} scope="row">{enLinea(c, ctx)}</th>
                      : <td key={k}>{enLinea(c, ctx)}</td>))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        return <p key={i}>{enLinea(b.texto, ctx)}</p>;
      })}
    </div>
  );
}

interface Contexto {
  idioma: Idioma;
  tarjetas: Tarjeta[];
  activa: string | null;
  alCitar?: (id: string) => void;
}

/** Quita una sección del Markdown y todo lo que cuelga de ella, hasta el siguiente encabezado. */
export function sinSeccion(bloques: Bloque[], patron: RegExp): Bloque[] {
  const salida: Bloque[] = [];
  let saltando = false;
  for (const b of bloques) {
    if (b.tipo === "h") saltando = patron.test(b.texto.trim());
    if (!saltando) salida.push(b);
  }
  return salida;
}

export type Bloque =
  | { tipo: "h"; texto: string }
  | { tipo: "p"; texto: string }
  | { tipo: "lista"; items: string[] }
  | { tipo: "numerada"; items: string[] }
  | { tipo: "tabla"; encabezados: string[]; filas: string[][] };

export function partir(md: string): Bloque[] {
  const salida: Bloque[] = [];
  let lista: string[] | null = null;
  let numerada: string[] | null = null;
  let parrafo: string[] = [];

  const cerrarParrafo = () => {
    if (parrafo.length) {
      salida.push({ tipo: "p", texto: parrafo.join(" ") });
      parrafo = [];
    }
  };
  const cerrarListas = () => {
    if (lista) { salida.push({ tipo: "lista", items: lista }); lista = null; }
    if (numerada) { salida.push({ tipo: "numerada", items: numerada }); numerada = null; }
  };

  // Las líneas de tabla se acumulan y se cierran juntas: una tabla es un bloque, no N párrafos.
  let filas: string[][] | null = null;
  const cerrarTabla = () => {
    if (!filas) return;
    // Una tabla de verdad trae cabecera y separador. Si no, NO se borra: se devuelve como texto.
    // Borrar en silencio es lo que dejó a un usuario de Essity con una respuesta que decía «aquí
    // está la tabla» seguida de nada — el modelo la había escrito y la pantalla se la comió.
    const sep = filas[1]?.every((c) => /^:?-{2,}:?$/.test(c.replace(/\s/g, "")));
    if (filas.length >= 2 && sep) {
      salida.push({ tipo: "tabla", encabezados: filas[0]!, filas: filas.slice(2) });
    } else {
      for (const f of filas) salida.push({ tipo: "p", texto: f.join(" | ") });
    }
    filas = null;
  };

  const lineas = md.split("\n");
  for (const cruda of lineas) {
    const linea = cruda.trimEnd();
    if (/^\s*\|/.test(linea)) {
      cerrarParrafo();
      cerrarListas();
      (filas ??= []).push(linea.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()));
      continue;
    }
    cerrarTabla();
    if (!linea.trim()) { cerrarParrafo(); cerrarListas(); continue; }
    const h = /^#{2,4}\s+(.*)$/.exec(linea);
    if (h) { cerrarParrafo(); cerrarListas(); salida.push({ tipo: "h", texto: h[1]!.trim() }); continue; }
    const vi = /^\s*[-*]\s+(.*)$/.exec(linea);
    if (vi) { cerrarParrafo(); if (numerada) { salida.push({ tipo: "numerada", items: numerada }); numerada = null; } (lista ??= []).push(vi[1]!); continue; }
    const nu = /^\s*\d{1,2}[.)]\s+(.*)$/.exec(linea);
    if (nu) { cerrarParrafo(); if (lista) { salida.push({ tipo: "lista", items: lista }); lista = null; } (numerada ??= []).push(nu[1]!); continue; }
    cerrarListas();
    parrafo.push(linea.trim());
  }
  cerrarParrafo();
  cerrarListas();
  cerrarTabla();
  return salida;
}

function enLinea(texto: string, ctx: Contexto): React.ReactNode[] {
  const nodos: React.ReactNode[] = [];
  const resto = texto;
  let clave = 0;
  // Primero las citas, que son lo único interactivo; luego negritas y código.
  CITA.lastIndex = 0;
  let ultimo = 0;
  let m: RegExpExecArray | null;
  const piezas: (string | { ev: string })[] = [];
  while ((m = CITA.exec(resto)) !== null) {
    if (m.index > ultimo) piezas.push(resto.slice(ultimo, m.index));
    piezas.push({ ev: `ev_${m[1]}` });
    ultimo = m.index + m[0].length;
  }
  piezas.push(resto.slice(ultimo));
  for (const p of piezas) {
    if (typeof p === "string") {
      nodos.push(...enfasis(p, clave));
      clave += 4;
    } else {
      const indice = ctx.tarjetas.findIndex((t) => t.id === p.ev);
      nodos.push(
        <ChipCita key={`ev${clave++}`} n={String(indice >= 0 ? indice + 1 : p.ev.split("_").at(-1))}
                  tarjeta={ctx.tarjetas[indice]} idioma={ctx.idioma}
                  activa={ctx.activa === p.ev} alAbrir={() => ctx.alCitar?.(p.ev)} />,
      );
    }
  }
  return nodos;
}

function enfasis(texto: string, base: number): React.ReactNode[] {
  const nodos: React.ReactNode[] = [];
  const patron = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let ultimo = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = patron.exec(texto)) !== null) {
    if (m.index > ultimo) nodos.push(texto.slice(ultimo, m.index));
    if (m[1]) nodos.push(<strong key={`b${base}${i++}`}>{m[1]}</strong>);
    else if (m[2]) nodos.push(<code key={`c${base}${i++}`}>{m[2]}</code>);
    ultimo = m.index + m[0].length;
  }
  if (ultimo < texto.length) nodos.push(texto.slice(ultimo));
  return nodos;
}
